/**
 * ShoppingAssistant.config.js
 *
 * Configuration for Constructor ASA (AI Shopping Assistant) component.
 * Contains preferences for hero images, starter suggestions, and API settings.
 *
 * CONFIGURATION PRIORITY:
 * 1. preferences.constructorIO.asa (from settings.json) - highest priority
 * 2. Defaults in this file - fallback
 *
 * The getAsaConfig() helper merges settings.json preferences with defaults.
 *
 * FEATURE FLAG + COOKIE OVERRIDE:
 * ASA can be enabled via:
 * 1. featureFlags.enableShoppingAssistant = true (master flag)
 * 2. ASAtest cookie = 'true' (override for testing, works regardless of master flag)
 *
 * Use isAsaEnabled() helper to check both conditions.
 */

import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';

// Default cookie name for ASA testing override
const DEFAULT_ASA_COOKIE_NAME = 'ASAtest';

/**
 * Get the ASA test cookie name from preferences or use default.
 * @param {Object} preferences - preferences object from Redux state
 * @returns {string} Cookie name
 */
export const getAsaCookieName = (preferences = {}) => {
    return preferences?.constructorIO?.asa?.cookieName || DEFAULT_ASA_COOKIE_NAME;
};

/**
 * Determines if ASA (Ask Penney) should be enabled.
 * Checks both the master feature flag AND the ASAtest cookie override.
 *
 * Logic:
 * - If enableShoppingAssistant feature flag is true → enabled
 * - If ASAtest cookie is set to 'true' → enabled (regardless of feature flag)
 * - Otherwise → disabled
 *
 * @param {Object} featureFlags - featureFlags object from Redux state
 * @param {Object} preferences - preferences object from Redux state
 * @returns {boolean} Whether ASA should be enabled
 */
export const isAsaEnabled = (featureFlags = {}, preferences = {}) => {
    // Master feature flag - if true, ASA is enabled
    const masterFlag = featureFlags?.enableShoppingAssistant || false;
    if (masterFlag) return true;

    // Cookie override - allows enabling ASA for testing regardless of master flag
    const cookieName = getAsaCookieName(preferences);
    const cookieValue = Cookies.load(cookieName);
    if (cookieValue === 'true') return true;

    return false;
};

// Hero image configurations for AskPenney chat
export const heroImages = {
    // Default hero image - Shopping items
    default: {
        url:
            'https://dam.catalystbrands.com/api/public/content/27d5fd9570be4e52ab66ae7c4d1a452b?v=a1f80a2f',
        alt: 'Ask Penney - AI Shopping Assistant',
        campaign: 'default',
    },
    // Bluey School Set (Back to School campaign) - alternate
    blueySchoolSet: {
        url:
            'https://dam.catalystbrands.com/api/public/content/5c834b9d-8244-42de-967b-8f7e3e42eeb5?v=461f43fd',
        alt: 'Bluey School Set - Back to school shopping',
        campaign: 'back-to-school',
    },
};

// Default hero configuration
export const defaultHeroConfig = heroImages.default;

// Hero content - title and description text
export const heroContent = {
    title: "Find exactly what you're looking for, faster with AI.",
    description:
        "Discover brands, spot trends, explore products, and find the perfect gift in seconds. Just describe what you're looking for.",
};

// Suggested prompts shown in the chat interface
export const suggestedPrompts = [
    'I need a jacket for a special occasion',
    'Help me find a gift',
    "I'm looking to refresh my bedroom",
    'Back to school shopping',
];

// Constructor ASA API configuration (defaults)
export const asaApiConfig = {
    // Default API key for JCPenney
    defaultApiKey: 'key_xT77K5VmpiVduqnj',
    // Default domain for conversational assistant
    defaultDomain: 'assistant_conversational',
    // Section for product searches
    defaultSection: 'Products',
    // Number of items per carousel (per search result event)
    numResultsPerEvent: 12,
    // Maximum number of carousels (search result events) to show
    numResultEvents: 3,
};

// View More URL configuration (defaults)
export const viewMoreConfig = {
    // Filters to ignore when building View More URL
    // These filters are internal to Constructor and shouldn't appear in customer-facing URLs
    ignoredFilters: [
        'group_id',
        'groupId',
        'category_id',
        'categoryId',
        'sort_by',
        'sort_order',
        'sortBy',
        'sortOrder',
    ],
};

// Conversation storage configuration (defaults)
export const conversationConfig = {
    // Storage key prefix for localStorage/sessionStorage
    storagePrefix: 'cio-asa-conversation:',
    // Maximum conversations to store before eviction (oldest conversations removed first)
    maxConversations: 50,
    // Maximum messages to store per conversation (removes oldest when exceeded)
    maxMessagesPerConversation: 15,
    // Default conversation ID for site-wide chat
    globalConversationId: 'global',
    // Storage type: 'session' (sessionStorage - cleared on browser close) or 'local' (localStorage - persists)
    storageType: 'session',
};

// Input field configuration (Figma SSUX-5980 node 719:12487)
export const inputConfig = {
    // Maximum character limit for user input
    maxLength: 500,
    // Placeholder text shown in empty input
    placeholder: "I'm looking for …",
    // Helper text shown below input
    helperText: 'Need ideas? Start with a style, occasion, or color.',
    // Warning message when character limit exceeded
    warningMessage:
        'You\'re writing a long input, which may result in a "no match" result. Shorten your query for a better response.',
};

// Loading messages configuration - rotating messages shown while ASA fetches results
export const loadingMessagesConfig = {
    // Array of messages to rotate through while loading
    messages: [
        'Penney is putting the pieces together...',
        '...Finding great deals...',
        '...Just a moment, while I look...',
        '...Great deals loading...',
        '...Searching for your perfect match...',
        '..One moment, matching you with great deals...',
    ],
    // Interval in milliseconds between message rotations
    rotationInterval: 3000,
};

// Disclaimer links configuration - URLs for privacy and legal notices
export const disclaimerConfig = {
    // Privacy Notice URL
    privacyNoticeUrl: 'https://www.jcpenney.com/m/privacy-notice',
    privacyNoticeText: 'Notice of Privacy Practices',
    // Legal Notice URL
    legalNoticeUrl: 'https://www.jcpenney.com/m/legal',
    legalNoticeText: 'Legal Notice',
    // Disclaimer message prefix
    disclaimerPrefix:
        "Our AI is learning and may make mistakes. By using Ask Penney, you agree to JCPenney's",
};

// Custom event names for component communication
export const eventNames = {
    openChat: 'askpenney:open',
    closeChat: 'askpenney:close',
    miniPdpOpen: 'miniPDP.open',
};

/**
 * Constructor ASA Beacon Data Attributes
 * Reference: https://docs.constructor.com/docs/integrating-with-constructor-behavioral-tracking-data-driven-event-tracking#ai-shopping-agent
 *
 * These data attributes are used for behavioral tracking with Constructor's ML platform.
 * They are automatically added to the corresponding UI elements.
 *
 * NOTE: asaBeacons stay in this config file only - NOT configurable via settings.json
 */
export const asaBeacons = {
    // Main container - wraps the entire ASA experience
    container: 'data-cnstrc-asa',

    // Input element where user types their query
    input: 'data-cnstrc-asa-input',

    // Submit button for ASA query
    submitButton: 'data-cnstrc-asa-submit-btn',

    // Suggestions container and individual suggestion buttons
    suggestions: 'data-cnstrc-asa-suggestions',
    suggestion: 'data-cnstrc-asa-suggestion',

    // Loader element shown while streaming (must remain in DOM while stream is open)
    loader: 'data-cnstrc-asa-loader',

    // Results container attributes
    intent: 'data-cnstrc-asa-intent', // User's query/intent that triggered the response
    intentResultId: 'data-cnstrc-asa-intent-result-id', // From response.intent_result_id
    intentNumResults: 'data-cnstrc-asa-intent-num-results', // Number of DISTINCT search query results (groups), NOT total items. Matches search_result_count from stream end segment.

    // Search result list container attributes (one per product group/carousel)
    search: 'data-cnstrc-asa-search',
    searchResultId: 'data-cnstrc-asa-search-result-id', // From response.result_id per data segment
    searchNumResults: 'data-cnstrc-asa-search-num-results', // Total items in THIS search result group

    // Item attributes (on each product card)
    itemId: 'data-cnstrc-item-id',
    itemName: 'data-cnstrc-item-name',
    itemVariationId: 'data-cnstrc-item-variation-id', // Optional, when variation shown (from data.variation_id)
    itemPrice: 'data-cnstrc-item-price', // Optional, when CTA button present

    // CTA button attribute (add_to_cart, add_to_wishlist, etc.)
    ctaButton: 'data-cnstrc-btn',
};

/**
 * Get ASA configuration merged with preferences from settings.json
 *
 * Priority: preferences.constructorIO.asa > defaults in this file
 *
 * @param {Object} preferences - preferences object from Redux state.context.preferences
 * @returns {Object} Merged configuration object
 *
 * Example settings.json structure:
 * {
 *   "preferences": {
 *     "constructorIO": {
 *       "common": { "indexKey": "..." },
 *       "asa": {
 *         "domain": "assistant_conversational",
 *         "numResultsPerEvent": 12,
 *         "numResultEvents": 3,
 *         "suggestedPrompts": ["..."],
 *         "heroContent": { "title": "...", "description": "..." },
 *         "inputConfig": { "maxLength": 500, "placeholder": "..." },
 *         "disclaimerConfig": { "privacyNoticeUrl": "..." },
 *         "viewMoreConfig": { "ignoredFilters": [...] }
 *       }
 *     }
 *   }
 * }
 */
export function getAsaConfig(preferences = {}) {
    const constructorIO = preferences?.constructorIO || {};
    const common = constructorIO.common || {};
    const asa = constructorIO.asa || {};

    return {
        // API config - merge with defaults
        apiKey: common.indexKey || asaApiConfig.defaultApiKey,
        domain: asa.domain || asaApiConfig.defaultDomain,
        section: asa.section || asaApiConfig.defaultSection,
        numResultsPerEvent: asa.numResultsPerEvent ?? asaApiConfig.numResultsPerEvent,
        numResultEvents: asa.numResultEvents ?? asaApiConfig.numResultEvents,

        // UI content - merge with defaults
        heroImages: asa.heroImages || heroImages,
        heroContent: { ...heroContent, ...(asa.heroContent || {}) },
        suggestedPrompts: asa.suggestedPrompts || suggestedPrompts,
        inputConfig: { ...inputConfig, ...(asa.inputConfig || {}) },
        loadingMessagesConfig: { ...loadingMessagesConfig, ...(asa.loadingMessagesConfig || {}) },
        disclaimerConfig: { ...disclaimerConfig, ...(asa.disclaimerConfig || {}) },

        // View More URL config - merge with defaults
        viewMoreConfig: { ...viewMoreConfig, ...(asa.viewMoreConfig || {}) },

        // Conversation config - merge with defaults
        conversationConfig: { ...conversationConfig, ...(asa.conversationConfig || {}) },

        // Event names - merge with defaults
        eventNames: { ...eventNames, ...(asa.eventNames || {}) },
    };
}

export default {
    heroImages,
    defaultHeroConfig,
    heroContent,
    suggestedPrompts,
    asaApiConfig,
    viewMoreConfig,
    conversationConfig,
    inputConfig,
    loadingMessagesConfig,
    disclaimerConfig,
    eventNames,
    asaBeacons,
    getAsaConfig,
};
